/**
 * template-utils.js - FileToQR 템플릿 유틸리티
 * 버전: 1.1.0
 * 최종 업데이트: 2025-07-25
 * 
 * 이 모듈은 Handlebars 템플릿 관련 유틸리티 함수를 제공합니다.
 * - Handlebars 라이브러리 로드
 * - 헬퍼 함수 등록
 * - 파셜(partial) 템플릿 로드 및 등록
 * - 템플릿 컴파일 및 렌더링
 */

import Handlebars from 'handlebars';
import i18n from './i18n-utils.js';
import { log } from './log-utils.js';

const TEMPLATE_UTILS_ID = 'TemplateUtils';
const TEMPLATE_MODULES = import.meta.glob('/components/partials/*.hbs', { query: '?raw', import: 'default', eager: true });

const templateCache = new Map();

/**
 * Handlebars 템플릿 관련 유틸리티를 관리하는 클래스
 */
class TemplateUtils {
  constructor() {
    this.isInitialized = false;
    this.templates = {};
  }

  /**
   * TemplateUtils 모듈을 초기화합니다.
   * Handlebars 헬퍼를 등록합니다.
   */
  init() {
    if (this.isInitialized) {
      log(TEMPLATE_UTILS_ID, 'Already initialized.');
      return;
    }
    log(TEMPLATE_UTILS_ID, 'Initialization started.');

    try {
      this.registerHelpers();
      this.registerPartials();
      this.isInitialized = true;
      log(TEMPLATE_UTILS_ID, 'Initialization completed successfully.');
    } catch (error) {
      console.error(`[${TEMPLATE_UTILS_ID}] Initialization failed:`, error);
      this.isInitialized = false;
    }
  }

  /**
   * 필요한 Handlebars 헬퍼들을 등록합니다.
   */
  registerHelpers() {
    log(TEMPLATE_UTILS_ID, 'Registering Handlebars helpers...');

    Handlebars.registerHelper('t', (key, options) => {
      return i18n.t(key, options.hash);
    });

    Handlebars.registerHelper('eq', (a, b) => a === b);
    Handlebars.registerHelper('lt', (a, b) => a < b);
    Handlebars.registerHelper('concat', (...args) => args.slice(0, -1).join(''));

    log(TEMPLATE_UTILS_ID, 'All Handlebars helpers registered.');
  }

  /**
   * 불러온 모든 템플릿 모듈을 Handlebars 파셜로 등록합니다.
   */
  registerPartials() {
    log(TEMPLATE_UTILS_ID, 'Registering partials...');
    for (const path in TEMPLATE_MODULES) {
      const partialName = path.split('/').pop().replace('.hbs', '');
      Handlebars.registerPartial(partialName, TEMPLATE_MODULES[path]);
    }
    log(TEMPLATE_UTILS_ID, 'All partials registered.');
  }

  /**
   * 지정된 경로의 템플릿을 렌더링합니다.
   * @param {string} templatePath - 렌더링할 템플릿 파일의 경로 (예: '/components/partials/header.hbs')
   * @param {object} context - 템플릿에 전달할 데이터 객체
   * @returns {string} 렌더링된 HTML 문자열
   */
  render(templatePath, context) {
    if (!this.isInitialized) {
      console.error(`[${TEMPLATE_UTILS_ID}] Not initialized. Call init() first.`);
      return '';
    }

    // context에 t 함수가 없으면 i18n.t를 자동으로 주입 (핸들바 템플릿에서 번역이 항상 동작하도록 보장)
    if (!context.t) {
      context.t = i18n.t.bind(i18n);
    }

    if (!this.templates[templatePath]) {
      const templateString = TEMPLATE_MODULES[templatePath];
      if (templateString !== undefined) {
        try {
          this.templates[templatePath] = Handlebars.compile(templateString);
          log(TEMPLATE_UTILS_ID, `Template '${templatePath}' from modules compiled and cached.`);
        } catch (error) {
          console.error(`[${TEMPLATE_UTILS_ID}] Error compiling template '${templatePath}':`, error);
          return '';
        }
      } else {
        console.error(`[${TEMPLATE_UTILS_ID}] Template with path '${templatePath}' not found in modules.`);
        return '';
      }
    }
    
    return this.templates[templatePath](context);
  }
}

const templateUtilsInstance = new TemplateUtils();

if (typeof window !== 'undefined') {
  window.FileToQR = window.FileToQR || {};
  window.FileToQR.TemplateUtils = templateUtilsInstance;
  log(TEMPLATE_UTILS_ID, 'Successfully assigned to window.FileToQR.TemplateUtils');
}

export default templateUtilsInstance; 